import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  mirrorChartMargin,
  mirrorHorizontalBarRadius,
  rtlChartProps,
} from "src/components/charts/chartRtl";
import { isArabicLanguage } from "src/utils/numberFormat";

/**
 * Mirrors Recharts geometry for Arabic across every Paid Media chart — overview, Meta Ads report
 * and Google Ads report.
 *
 * The chart surfaces themselves are pinned to `direction: ltr` (see the `.recharts-surface` rules
 * in `PaidMediaDashboard.scss` and the overview `_base.scss`), because Recharts positions ticks,
 * bars and labels with absolute SVG coordinates and `text-anchor` that resolve against the inline
 * base direction. Inheriting `dir="rtl"` flips that anchoring under LTR geometry instead of
 * mirroring the chart, which is what left Arabic axis labels sitting on top of their own bars. So
 * the flip is applied deliberately through the props below: the value axis moves to the right
 * edge, category and value axes count right-to-left, and bars grow and get labelled from the right.
 *
 * Read once per render from `useTranslation`, so a language switch re-mirrors every chart.
 */
export const usePaidMediaChartRtl = () => {
  const { i18n } = useTranslation();
  const isRTL = isArabicLanguage(i18n.resolvedLanguage ?? i18n.language);

  return useMemo(() => {
    const { yAxisOrientation, oppositeYAxisOrientation, axisReversed, labelPosition } =
      rtlChartProps(isRTL);

    return {
      isRTL,
      yAxisOrientation,
      oppositeYAxisOrientation,
      axisReversed,
      labelPosition,
      /** Rounded corners belong on the bar's growing end (`layout="vertical"` charts). */
      horizontalBarRadius: (radius: number) => mirrorHorizontalBarRadius(radius, isRTL),
      /** Swaps the left/right chart margins so the flipped axis keeps its gutter. */
      margin: <T extends { left?: number; right?: number }>(margin: T) =>
        mirrorChartMargin(margin, isRTL),
    };
  }, [isRTL]);
};

export type PaidMediaChartRtl = ReturnType<typeof usePaidMediaChartRtl>;
